import { call, put, select, takeLatest } from "typed-redux-saga";
import { Action, AnyAction } from "typescript-fsa";

import { ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { ApplicationActions } from "@com.mgmtp.a12.client/client-core/lib/core/application";
import { EngineState, Events, ModelSelectors } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import {
	FormActivity,
	FormEngineActions,
	FormEngineSelectors
} from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { DocumentServiceFactory, EntityInstancePath } from "@com.mgmtp.a12.kernel/kernel-md-facade";
import { LoggerFactory } from "@com.mgmtp.a12.utils/utils-logging";

import { RESOURCE_KEYS } from "../../../localization";
import { validateActivity } from "../../../utilities/activityUtils";
import { putErrorNotification } from "../../../utilities/notificationUtils";
import { executeWithCompleteValidation, validateActivityForSave } from "../../../utilities/validationUtils";
import {
	CUSTOM_BACK_TO_PREVIOUS,
	CUSTOM_BACK_TO_START,
	CUSTOM_PROCEED
} from "../../../utilities/variables/customEventNames";
import {
	NAVIGATE_WEITER,
	NAVIGATE_ZURUECK,
	NAVIGATE_ZURUECKZUMSTART,
	NAVIGATION_PATH
} from "../../../utilities/variables/modulfRuntimeAnnotations";
import { VALIDATION_ACTIONS, isDocumentContainingA12wf } from "../../../utilities/workflowUtils";
import { FormModelUtils } from "../../../workaround/internal/form-model-utils";

import { customCompleteTask } from "../actions/workflowsActions";

const documentService = new DocumentServiceFactory().getDocumentService();
const logger = LoggerFactory.getLogger("modulf");

function isTransitionAction(action: AnyAction): action is Action<{
	readonly activityId: string;
	readonly engineEvent: Action<Events.EventButtonPayload>;
}> {
	return (
		FormEngineActions.event.match(action) &&
		Events.eventButtonTriggered.match(action.payload.engineEvent) &&
		[CUSTOM_PROCEED, CUSTOM_BACK_TO_START, CUSTOM_BACK_TO_PREVIOUS].includes(
			action.payload.engineEvent.payload.name
		)
	);
}

export function* customProceedSaga() {
	yield* takeLatest(isTransitionAction, function* (action) {
		yield* put(ApplicationActions.setBusy(true));

		const activityId = action.payload.activityId;
		const data = yield* select(ActivitySelectors.data(activityId));
		const engineState = yield* select(FormEngineSelectors.engineState(activityId));

		if (
			!FormActivity.Data.SingleDocumentData.isInstance(data) ||
			!isDocumentContainingA12wf(data.document) ||
			!engineState
		) {
			if (!engineState) {
				logger.error("Couldn't get EngineState");
			}
			return;
		}

		const formModel = ModelSelectors.formModel()(engineState);
		const pathElements = FormModelUtils.findPathElementsByFormModelPath(
			formModel,
			action.payload.engineEvent.payload.buttonPath
		);
		const eventButton = pathElements[pathElements.length - 1];

		let isValid;
		if (action.payload.engineEvent.payload.name === CUSTOM_PROCEED) {
			if (FormModelUtils.isAnnotatedInstance(eventButton)) {
				const gatewayEntscheidung = eventButton?.annotations?.find(e => e.name === NAVIGATION_PATH)?.value;
				if (gatewayEntscheidung) {
					yield* call(
						setGatewayEntscheidung,
						engineState,
						gatewayEntscheidung,
						data,
						activityId,
						NAVIGATE_WEITER
					);
				}
			}
			isValid = yield* call(
				executeWithCompleteValidation,
				activityId,
				function* () {
					return yield* call(validateActivity, activityId);
				},
				VALIDATION_ACTIONS.NULL
			);
		}

		if (action.payload.engineEvent.payload.name === CUSTOM_BACK_TO_START) {
			isValid = yield* call(
				setGatewayAndValidate,
				engineState,
				data,
				activityId,
				NAVIGATE_ZURUECKZUMSTART,
				eventButton
			);
		}

		if (action.payload.engineEvent.payload.name === CUSTOM_BACK_TO_PREVIOUS) {
			isValid = yield* call(setGatewayAndValidate, engineState, data, activityId, NAVIGATE_ZURUECK, eventButton);
		}

		if (isValid) {
			yield* put(
				customCompleteTask({
					activityId: action.payload.activityId,
					document: data.document,
					docRef: data.document.id
				})
			);
		} else {
			yield* put(ApplicationActions.setBusy(false));
		}
	});
}

function* handleError(error: string, group: string) {
	yield* call(
		putErrorNotification,
		RESOURCE_KEYS.features.notification.error.gatewayEntscheidung,
		error,
		undefined,
		group
	);
}

function createInstancePath(path: string): EntityInstancePath {
	return path
		.split("/")
		.map(e => ({ elementName: e, index: 1 }))
		.slice(1);
}

function* setGatewayEntscheidung(
	engineState: EngineState,
	gatewayEntscheidungPath: string,
	data: FormActivity.Data.SingleDocumentData,
	activityId: string,
	entscheidung: string
) {
	const documentModel = ModelSelectors.documentModel()(engineState);
	const gatewayEntscheidungInstancePath = createInstancePath(gatewayEntscheidungPath);
	const navigationEntscheidung = documentService.updateEntityInstance(
		data.document,
		gatewayEntscheidungInstancePath,
		entscheidung,
		documentModel
	);
	yield* put(
		ActivityActions.setData({
			activityId,
			data: { ...data, document: navigationEntscheidung }
		})
	);
}

function* setGatewayAndValidate(
	engineState: EngineState,
	data: FormActivity.Data.SingleDocumentData,
	activityId: string,
	entscheidung: string,
	eventButton: object
) {
	if (!FormModelUtils.isAnnotatedInstance(eventButton)) {
		yield* call(handleError, "No annotations on button found.", `setGatewayAndValidate:${data.document.id}`);
		return;
	}
	const gatewayEntscheidungPath = eventButton?.annotations?.find(e => e.name === NAVIGATION_PATH)?.value;
	if (!gatewayEntscheidungPath) {
		yield* call(
			handleError,
			`Annotation ${NAVIGATION_PATH} not found.`,
			`setGatewayAndValidate:${data.document.id}`
		);
		return;
	}
	yield* call(setGatewayEntscheidung, engineState, gatewayEntscheidungPath, data, activityId, entscheidung);
	return yield* call(validateActivityForSave, activityId);
}
